import React from "react";
import { Box } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

// Donut chart drawn as inline vector markup.
//
// Geometry (matches gifted-charts-core's PieChart/main.js, the reference this
// was measured against):
//  - Each slice is a FULL pie wedge from center → outer radius; the donut hole
//    is a separate opaque circle painted on top (NOT an annular path).
//  - Point map is (cx·(1+sinθ), cy·(1−cosθ)) with θ = 2π·frac + initialAngle,
//    so initialAngle = -π/2 starts the first slice at 9 o'clock, sweeping
//    clockwise (SVG sweep-flag 1). large-arc-flag = value > total/2 ? 1 : 0.
//  - viewBox = "-E -E (R+E)*2 (R+E)*2" where E = extraRadius = R/10 when
//    focusOnPress/sectionAutoFocus is set; pie center is user-space (R,R).
//  - Radial gradient per slice, userSpaceOnUse, centered on the pie center:
//    stop 0% = gradientCenterColor, stop 100% = color.
// The chart is static: slices carry no press-to-focus interaction and there is
// no enter animation — it draws the resting geometry only.

export type DonutChartDatum = Omittable<{
  value: number;
  color: string;
  gradientCenterColor: string;
  /** Always true at our call sites; kept for API parity. */
  showGradient?: boolean;
}>;

export type DonutChartProps = Omittable<{
  data: DonutChartDatum[];
  radius: number;
  innerRadius: number;
  /** Fill for the center hole (background color). */
  innerCircleColor: string;
  /** Radians. Matches gifted's `initialAngle`. Defaults to -π/2 (9 o'clock). */
  initialAngle?: number;
  /** gifted flags — only affect resting layout via extraRadius. */
  focusOnPress?: boolean;
  sectionAutoFocus?: boolean;
  centerLabelComponent?: () => React.ReactNode;
}>;

const TWO_PI = Math.PI * 2;

function pointOnPie(cx: number, cy: number, theta: number): [number, number] {
  return [cx * (1 + Math.sin(theta)), cy * (1 - Math.cos(theta))];
}

export function DonutChart({
  data,
  radius,
  innerRadius,
  innerCircleColor,
  initialAngle = -Math.PI / 2,
  focusOnPress,
  sectionAutoFocus,
  centerLabelComponent,
}: DonutChartProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  // gifted: extraRadius = radius/10 when focus is enabled (symmetric padding).
  const extraRadius = focusOnPress || sectionAutoFocus ? radius / 10 : 0;
  const cx = radius;
  const cy = radius;
  const size = (radius + extraRadius) * 2;
  const viewBox = `${-extraRadius} ${-extraRadius} ${size} ${size}`;

  // Cumulative start fractions: C[-1] = 0, C[i] = (v0+…+vi)/total.
  let acc = 0;
  const slices = data.map((d, i) => {
    const startFrac = acc;
    acc += total > 0 ? d.value / total : 0;
    const endFrac = acc;
    const thetaStart = TWO_PI * startFrac + initialAngle;
    const thetaEnd = TWO_PI * endFrac + initialAngle;
    const [sx, sy] = pointOnPie(cx, cy, thetaStart);
    const [ax, ay] = pointOnPie(cx, cy, thetaEnd);
    const largeArc = d.value > total / 2 ? 1 : 0;
    const path = `M ${cx} ${cy} L ${sx} ${sy} A ${radius} ${radius} 0 ${largeArc} 1 ${ax} ${ay} L ${cx} ${cy}`;
    return { path, datum: d, index: i };
  });

  const gradR = radius + extraRadius;

  return (
    <Box width={size} height={size} position="relative">
      <svg width={size} height={size} viewBox={viewBox}>
        <defs>
          {slices.map(({ datum, index }) => (
            <radialGradient
              key={index}
              id={`donut-grad-${index}`}
              gradientUnits="userSpaceOnUse"
              cx={cx}
              cy={cy}
              r={gradR}
              fx={cx}
              fy={cy}
            >
              <stop offset="0%" stopColor={datum.gradientCenterColor} stopOpacity={1} />
              <stop offset="100%" stopColor={datum.color} stopOpacity={1} />
            </radialGradient>
          ))}
        </defs>
        {total > 0 &&
          slices.map(({ path, index }) => (
            <path key={index} d={path} fill={`url(#donut-grad-${index})`} />
          ))}
        <circle cx={cx} cy={cy} r={innerRadius} fill={innerCircleColor} />
      </svg>
      {centerLabelComponent && (
        <Box
          pointerEvents="none" position="absolute"
          left={extraRadius + radius - innerRadius}
          top={extraRadius + radius - innerRadius}
          width={innerRadius * 2}
          height={innerRadius * 2}
          align="center"
          justify="center"
        >
          {centerLabelComponent()}
        </Box>
      )}
    </Box>
  );
}
